import { memo } from "react";
import { FormattedMessage } from "react-intl";
import { Box, FormControlLabel, Switch } from "@mui/material";
import AppButton from "../../components/AppButton";

import CustomerSelector from "./Inputs/CustomerSelector";
import BannerColorSelector from "./Inputs/BannerColorSelector";
import LanguageSelector from "./Inputs/LanguageSelector";
import { TooltipLabel } from "../../components/Tooltips";
import {
  CriticityType,
  DisplayDelayType,
  FormErrorsType,
  LanguagesType,
} from "../interfaces";
import PinningCheckbox from "./Inputs/PinningCheckbox";
import DisplayDelaySelector from "./Inputs/DisplayDelaySelector";
import NameInput from "./Inputs/NameInput";

const Panel = memo(
  ({
    bannerPublishing,
    formErrors,
    isActive,
    isPinned,
    onActiveToggle,
    onBannerDisplayChange,
    onCustomerChange,
    onCriticityChange,
    onLanguageChange,
    onNameChange,
    onPinningChange,
    onSubmit,
  }: {
    bannerPublishing: boolean;
    formErrors: FormErrorsType;
    isActive: boolean;
    isPinned: boolean;
    onActiveToggle: (active: boolean) => void;
    onBannerDisplayChange: (pinning: DisplayDelayType) => void;
    onCustomerChange: (customers: number[] | "all") => void;
    onCriticityChange: (contents: CriticityType) => void;
    onLanguageChange: (languages: LanguagesType[]) => void;
    onNameChange: (name: string) => void;
    onPinningChange: (pinning: boolean) => void;
    onSubmit: () => void;
  }) => {
    return (
      <Box
        sx={(theme) => ({
          height: "fit-content",
          width: "30%",
          backgroundColor: "background.paper",
          pt: 1,
          px: 2,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          borderRadius: "6px",
          boxSizing: "border-box",
          gap: 2,
          boxShadow: theme.vars.palette.shadow.main,
          position: "sticky",
          top: "16px",
        })}
      >
        <NameInput onNameChange={onNameChange} error={formErrors.name} />
        <CustomerSelector
          onCustomerChange={onCustomerChange}
          error={formErrors.customers}
          limitTags={2}
        />
        <LanguageSelector
          onLanguageChange={onLanguageChange}
          error={formErrors.languages}
        />
        <BannerColorSelector
          onCriticityChange={onCriticityChange}
          error={formErrors.criticity}
        />
        <PinningCheckbox onPinningChange={onPinningChange} />
        {!isPinned && (
          <DisplayDelaySelector
            onBannerDisplayChange={onBannerDisplayChange}
            isPinned={isPinned}
            error={formErrors.nb_days_before_redisplay}
          />
        )}
        <FormControlLabel
          sx={{ marginLeft: 0 }}
          control={
            <Switch
              checked={isActive}
              onChange={(_, checked) => onActiveToggle(checked)}
              size="small"
            />
          }
          label={
            <TooltipLabel
              label={<FormattedMessage id="BANNER_DISPLAY_FOR_ALL" />}
              tooltipLabel="BANNER_PANEL_ACTIVATE_TOOLTIP"
            />
          }
          labelPlacement="end"
        />
        <AppButton onClick={() => onSubmit()} loading={bannerPublishing}>
          <FormattedMessage id="SUBMIT" />
        </AppButton>
      </Box>
    );
  }
);

export default Panel;
